import { ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Text } from './Text';
import { useT } from '../../modules/i18n';

export interface ExternalToolCardProps {
  name: string;
  description: ReactNode;
  url: string;
  /** A lucide icon shown in the brand-tinted badge. */
  icon?: ReactNode;
  className?: string;
}

/**
 * External-tool link card — an icon badge, name and description; the whole
 * card is a link that opens the tool in a new tab, with an out-arrow
 * affordance. Used on the External Tools page.
 */
export const ExternalToolCard = ({
  name,
  description,
  url,
  icon,
  className,
}: ExternalToolCardProps) => {
  const t = useT();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('ui.externalToolCard.openAriaLabel', { name })}
      className={cx(
        'group flex flex-col gap-2.5 rounded-xl border border-default bg-surface p-4 shadow-xs no-underline transition',
        'hover:border-strong hover:shadow-md focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)] outline-none',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {icon && (
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-solid/12 text-brand-text">
            {icon}
          </span>
        )}
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <Text variant="body" as="h3" className="truncate font-semibold">
            {name}
          </Text>
          <Icon
            as={ArrowUpRight}
            size="sm"
            className="shrink-0 text-fg-muted transition-colors group-hover:text-brand-text"
          />
        </span>
      </div>
      <Text variant="body" tone="tertiary" className="line-clamp-2">
        {description}
      </Text>
    </a>
  );
};
